/**
 * ConnectionLinksManager Component
 * 
 * Manages and visualizes connection links between:
 * - Solution Inputs <-> Situation Resources
 * - Solution Benefits <-> Situation Needs
 * 
 * Features:
 * - Visual link representation
 * - Auto-detection of potential matches
 * - Manual link creation/editing
 * - Link strength indicators (full/partial/none)
 */

import React, { useState, useCallback } from 'react';
import { 
  Modal, 
  Table, 
  Button, 
  Tag, 
  Select, 
  Tooltip,
  Empty,
  Popconfirm,
  message,
  Tabs,
} from 'antd';
import {
  LinkOutlined,
  DisconnectOutlined,
  SyncOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { 
  ConnectionLink,
  ConnectionMatchType,
  SolutionInput,
  SolutionBenefit,
  SituationResource,
  SituationNeed,
} from '../../types/marketFit';
import { useMarketFit } from '../../contexts/MarketFitContext';
import styles from './ConnectionLinksManager.module.css';

interface ConnectionLinksManagerProps {
  visible: boolean;
  onClose: () => void;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const MatchTypeTag: React.FC<{ type: ConnectionMatchType }> = ({ type }) => {
  const config = {
    full: { color: '#22c55e', icon: <CheckCircleOutlined />, label: 'Full' },
    partial: { color: '#fbbf24', icon: <ExclamationCircleOutlined />, label: 'Partial' },
    none: { color: '#ef4444', icon: <CloseCircleOutlined />, label: 'None' },
  };
  
  const { color, icon, label } = config[type];
  
  return (
    <Tag color={color} icon={icon}>
      {label}
    </Tag>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ConnectionLinksManager: React.FC<ConnectionLinksManagerProps> = ({
  visible,
  onClose,
}) => {
  const {
    solution,
    situation,
    connectionLinks,
    addInputResourceLink,
    removeInputResourceLink,
    updateInputResourceLink,
    addBenefitNeedLink,
    removeBenefitNeedLink,
    updateBenefitNeedLink,
    autoDetectLinks,
    clearAllLinks,
  } = useMarketFit();

  const [activeTab, setActiveTab] = useState<'inputs' | 'benefits'>('inputs');
  const [addLinkModalVisible, setAddLinkModalVisible] = useState(false);
  const [linkType, setLinkType] = useState<'input' | 'benefit'>('input');
  const [selectedFromId, setSelectedFromId] = useState<string | null>(null);
  const [selectedToId, setSelectedToId] = useState<string | null>(null);
  const [selectedMatchType, setSelectedMatchType] = useState<ConnectionMatchType>('full');

  const inputs = solution?.inputs ?? [];
  const benefits = solution?.benefits ?? [];
  const resources = situation?.resources ?? [];
  const needs = situation?.needs ?? [];
  const inputLinks = connectionLinks.solutionInputToSituationResourceLinks;
  const benefitLinks = connectionLinks.solutionBenefitToSituationNeedLinks;

  // Handle auto-detect
  const handleAutoDetect = useCallback(() => {
    autoDetectLinks();
    void message.success('Links auto-detected based on name/description similarity');
  }, [autoDetectLinks]);

  // Handle clear all
  const handleClearAll = useCallback(() => {
    clearAllLinks();
    void message.success('All links cleared');
  }, [clearAllLinks]);

  // Handle add link
  const handleAddLink = () => {
    if (!selectedFromId || !selectedToId) {
      void message.error('Please select both source and target');
      return;
    }

    if (linkType === 'input') {
      addInputResourceLink(selectedFromId, selectedToId, selectedMatchType);
    } else {
      addBenefitNeedLink(selectedFromId, selectedToId, selectedMatchType);
    }

    void message.success('Link added');
    setAddLinkModalVisible(false);
    resetLinkForm();
  };

  const resetLinkForm = () => {
    setSelectedFromId(null);
    setSelectedToId(null);
    setSelectedMatchType('full');
  };

  const openAddLinkModal = (type: 'input' | 'benefit') => {
    setLinkType(type);
    resetLinkForm();
    setAddLinkModalVisible(true);
  };

  // Input-Resource link columns
  const inputLinkColumns = [
    {
      title: 'Solution Input',
      dataIndex: 'fromId',
      key: 'fromId',
      render: (fromId: string) => {
        const input = inputs.find(i => i.id === fromId);
        return (
          <div className={styles.linkItem}>
            <span className={styles.linkItemName}>{input?.name ?? 'Unknown'}</span>
            {input?.isRequired && (
              <Tag color="red" className={styles.smallTag}>Required</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Match',
      dataIndex: 'matchType',
      key: 'matchType',
      width: 100,
      render: (matchType: ConnectionMatchType, record: ConnectionLink) => (
        <Select
          value={matchType}
          onChange={(value) => updateInputResourceLink(record.id, { matchType: value })}
          size="small"
          className={styles.matchSelect}
        >
          <Select.Option value="full">
            <MatchTypeTag type="full" />
          </Select.Option>
          <Select.Option value="partial">
            <MatchTypeTag type="partial" />
          </Select.Option>
          <Select.Option value="none">
            <MatchTypeTag type="none" />
          </Select.Option>
        </Select>
      ),
    },
    {
      title: 'Situation Resource',
      dataIndex: 'toId',
      key: 'toId',
      render: (toId: string) => {
        const resource = resources.find(r => r.id === toId);
        return (
          <div className={styles.linkItem}>
            <span className={styles.linkItemName}>{resource?.name ?? 'Unknown'}</span>
            {!resource?.isExisting && (
              <Tag color="orange" className={styles.smallTag}>New</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: ConnectionLink) => (
        <Popconfirm
          title="Remove this link?"
          onConfirm={() => {
            removeInputResourceLink(record.id);
            void message.success('Link removed');
          }}
          okText="Remove"
          cancelText="Cancel"
        >
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
          />
        </Popconfirm>
      ),
    },
  ];

  // Benefit-Need link columns
  const benefitLinkColumns = [
    {
      title: 'Solution Benefit',
      dataIndex: 'fromId',
      key: 'fromId',
      render: (fromId: string) => {
        const benefit = benefits.find(b => b.id === fromId);
        return (
          <div className={styles.linkItem}>
            <span className={styles.linkItemName}>{benefit?.name ?? 'Unknown'}</span>
          </div>
        );
      },
    },
    {
      title: 'Match',
      dataIndex: 'matchType',
      key: 'matchType',
      width: 100,
      render: (matchType: ConnectionMatchType, record: ConnectionLink) => (
        <Select
          value={matchType}
          onChange={(value) => updateBenefitNeedLink(record.id, { matchType: value })}
          size="small"
          className={styles.matchSelect}
        >
          <Select.Option value="full">
            <MatchTypeTag type="full" />
          </Select.Option>
          <Select.Option value="partial">
            <MatchTypeTag type="partial" />
          </Select.Option>
          <Select.Option value="none">
            <MatchTypeTag type="none" />
          </Select.Option>
        </Select>
      ),
    },
    {
      title: 'Situation Need',
      dataIndex: 'toId',
      key: 'toId',
      render: (toId: string) => {
        const need = needs.find(n => n.id === toId);
        return (
          <div className={styles.linkItem}>
            <span className={styles.linkItemName}>{need?.name ?? 'Unknown'}</span>
            <Tag 
              color={
                need?.priority === 'high' ? 'red' : 
                need?.priority === 'medium' ? 'orange' : 'green'
              } 
              className={styles.smallTag}
            >
              {need?.priority ?? 'Unknown'}
            </Tag>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: ConnectionLink) => (
        <Popconfirm
          title="Remove this link?"
          onConfirm={() => {
            removeBenefitNeedLink(record.id);
            void message.success('Link removed');
          }}
          okText="Remove"
          cancelText="Cancel"
        >
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
          />
        </Popconfirm>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'inputs',
      label: (
        <span className={styles.tabLabel}>
          <LinkOutlined />
          Input → Resource ({inputLinks.length})
        </span>
      ),
      children: (
        <div className={styles.tabContent}>
          <div className={styles.tabHeader}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openAddLinkModal('input')}
              size="small"
            >
              Add Link
            </Button>
          </div>
          {inputLinks.length === 0 ? (
            <Empty 
              description="No input-resource links defined"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Table
              dataSource={inputLinks}
              columns={inputLinkColumns}
              rowKey="id"
              pagination={false}
              size="small"
              className={styles.linkTable}
            />
          )}
        </div>
      ),
    },
    {
      key: 'benefits',
      label: (
        <span className={styles.tabLabel}>
          <LinkOutlined />
          Benefit → Need ({benefitLinks.length})
        </span>
      ),
      children: (
        <div className={styles.tabContent}>
          <div className={styles.tabHeader}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openAddLinkModal('benefit')}
              size="small"
            >
              Add Link
            </Button>
          </div>
          {benefitLinks.length === 0 ? (
            <Empty 
              description="No benefit-need links defined"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Table
              dataSource={benefitLinks}
              columns={benefitLinkColumns}
              rowKey="id"
              pagination={false}
              size="small"
              className={styles.linkTable}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <div className={styles.modalTitle}>
            <LinkOutlined />
            <span>Connection Links Manager</span>
          </div>
        }
        open={visible}
        onCancel={onClose}
        width={800}
        footer={
          <div className={styles.modalFooter}>
            <div className={styles.footerLeft}>
              <Tooltip title="Automatically detect potential links based on name/description similarity">
                <Button 
                  icon={<SyncOutlined />}
                  onClick={handleAutoDetect}
                >
                  Auto-Detect Links
                </Button>
              </Tooltip>
              <Popconfirm
                title="Clear all connection links?"
                description="This will remove all input-resource and benefit-need links."
                onConfirm={handleClearAll}
                okText="Clear All"
                cancelText="Cancel"
              >
                <Button 
                  danger
                  icon={<DisconnectOutlined />}
                >
                  Clear All
                </Button>
              </Popconfirm>
            </div>
            <Button onClick={onClose}>Close</Button>
          </div>
        }
        className={styles.linksModal}
      >
        <div className={styles.summary}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Input Links:</span>
            <span className={styles.summaryValue}>{inputLinks.length} / {inputs.length}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Benefit Links:</span>
            <span className={styles.summaryValue}>{benefitLinks.length}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Needs Covered:</span>
            <span className={styles.summaryValue}>
              {new Set(benefitLinks.map(l => l.toId)).size} / {needs.length}
            </span>
          </div>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'inputs' | 'benefits')}
          items={tabItems}
          className={styles.tabs}
        />
      </Modal>

      {/* Add Link Modal */}
      <Modal
        title={linkType === 'input' ? 'Add Input-Resource Link' : 'Add Benefit-Need Link'}
        open={addLinkModalVisible}
        onOk={handleAddLink}
        onCancel={() => setAddLinkModalVisible(false)}
        okText="Add Link"
        className={styles.addLinkModal}
      >
        <div className={styles.addLinkForm}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              {linkType === 'input' ? 'Solution Input' : 'Solution Benefit'}
            </label>
            <Select
              value={selectedFromId}
              onChange={setSelectedFromId}
              placeholder={`Select ${linkType === 'input' ? 'input' : 'benefit'}`}
              style={{ width: '100%' }}
            >
              {linkType === 'input' 
                ? inputs.map(input => (
                    <Select.Option key={input.id} value={input.id}>
                      {input.name}
                    </Select.Option>
                  ))
                : benefits.map(benefit => (
                    <Select.Option key={benefit.id} value={benefit.id}>
                      {benefit.name}
                    </Select.Option>
                  ))
              }
            </Select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Match Type</label>
            <Select
              value={selectedMatchType}
              onChange={setSelectedMatchType}
              style={{ width: '100%' }}
            >
              <Select.Option value="full">Full Match</Select.Option>
              <Select.Option value="partial">Partial Match</Select.Option>
              <Select.Option value="none">No Match (Manual Override)</Select.Option>
            </Select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              {linkType === 'input' ? 'Situation Resource' : 'Situation Need'}
            </label>
            <Select
              value={selectedToId}
              onChange={setSelectedToId}
              placeholder={`Select ${linkType === 'input' ? 'resource' : 'need'}`}
              style={{ width: '100%' }}
            >
              {linkType === 'input'
                ? resources.map(resource => (
                    <Select.Option key={resource.id} value={resource.id}>
                      {resource.name}
                    </Select.Option>
                  ))
                : needs.map(need => (
                    <Select.Option key={need.id} value={need.id}>
                      {need.name}
                    </Select.Option>
                  ))
              }
            </Select>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ConnectionLinksManager;
